import {
  Dialog,
  DialogProps,
  FormControllerProvider,
  FormControllerProviderProps,
} from "common/components";
import { FlowDialogSize } from "core/api";
import { LoggingErrorBoundary } from "error/components";

export interface FlowActionDialogProps
  extends Omit<DialogProps, "open" | "onClose"> {
  /** If dialog should be open */
  open?: boolean;
  /** Flow dialog size */
  size?: FlowDialogSize;
  onClose(): void;
  evaluateOnChange: FormControllerProviderProps["evaluateOnChange"];
}

const dialogHeightMap: Record<FlowDialogSize, number | string | undefined> = {
  AUTO: undefined,
  FULLSCREEN: "100vh",
  SELFCARE_SMALL: undefined,
  NORMAL: 500,
  LARGE: 800,
};

const dialogWidthMap: Record<FlowDialogSize, DialogProps["maxWidth"]> = {
  AUTO: undefined,
  FULLSCREEN: undefined,
  SELFCARE_SMALL: "sm",
  NORMAL: "md",
  LARGE: "lg",
};

/** Flow action dialog */
export function FlowActionDialog({
  size = FlowDialogSize.NORMAL,
  open = true,
  onClose,
  evaluateOnChange,
  ...props
}: FlowActionDialogProps): React.ReactElement {
  const _onClose: DialogProps["onClose"] = (_, reason) => {
    if (reason === "escapeKeyDown") {
      onClose();
    }
  };
  return (
    <FormControllerProvider evaluateOnChange={evaluateOnChange}>
      <LoggingErrorBoundary onError={() => onClose()}>
        <Dialog
          {...props}
          open={open}
          onClose={_onClose}
          fullScreen={size === FlowDialogSize.FULLSCREEN}
          fullWidth={
            size !== FlowDialogSize.FULLSCREEN && size !== FlowDialogSize.AUTO
          }
          maxWidth={dialogWidthMap[size]}
          PaperProps={{ sx: { height: dialogHeightMap[size] } }}
        />
      </LoggingErrorBoundary>
    </FormControllerProvider>
  );
}
